import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BankingState, ExtractPendingResponse } from './types';
import type { ClickSource } from './constants';

const initialState: BankingState = {
    pendingCount: 0,
    inFlightCount: 0,
    extractedCount: 0,
    // Default true so the tab stays visible if the status endpoint is not yet deployed
    // or while the first fetch is in flight. Backend authoritatively overrides on fetch success.
    showBankingTab: true,
    bannerDismissedForOrgId: null,
    currentOrgId: null,
    loading: false,
    triggering: false,
    error: null,
};

const bankingSlice = createSlice({
    name: 'banking',
    initialState,
    reducers: {
        fetchBankingPendingStatus: (state, _action: PayloadAction<{ orgId: string }>) => {
            state.loading = true;
            state.error = null;
        },
        fetchBankingPendingStatusSuccess: (
            state,
            action: PayloadAction<{
                pendingCount: number;
                inFlightCount: number;
                extractedCount: number;
                showBankingTab: boolean;
                orgId: string;
            }>,
        ) => {
            const { orgId, ...rest } = action.payload;
            state.loading = false;
            // AC2: dismissal resets when new uploads bump pendingCount upward — banner reappears for new files.
            // Only the dismissal for THIS org is cleared; another org's dismissal is unaffected.
            if (rest.pendingCount > state.pendingCount && state.bannerDismissedForOrgId === orgId) {
                state.bannerDismissedForOrgId = null;
            }
            state.pendingCount = rest.pendingCount;
            state.inFlightCount = rest.inFlightCount;
            state.extractedCount = rest.extractedCount;
            state.showBankingTab = rest.showBankingTab;
            state.currentOrgId = orgId;
        },
        fetchBankingPendingStatusFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        triggerExtractPending: (state, _action: PayloadAction<{ orgId: string; clickSource: ClickSource }>) => {
            state.triggering = true;
            state.error = null;
        },
        triggerExtractPendingSuccess: (state, _action: PayloadAction<ExtractPendingResponse>) => {
            state.triggering = false;
        },
        triggerExtractPendingFailure: (state, action: PayloadAction<string>) => {
            state.triggering = false;
            state.error = action.payload;
        },
        dismissBanner: (state, action: PayloadAction<{ orgId: string }>) => {
            state.bannerDismissedForOrgId = action.payload.orgId;
        },
        resetBankingState: () => initialState,
    },
});

export const {
    fetchBankingPendingStatus,
    fetchBankingPendingStatusSuccess,
    fetchBankingPendingStatusFailure,
    triggerExtractPending,
    triggerExtractPendingSuccess,
    triggerExtractPendingFailure,
    dismissBanner,
    resetBankingState,
} = bankingSlice.actions;

export default bankingSlice.reducer;
